import {
  FormModal,
  FormField,
  FormInput,
  FormSelect,
  FormTextarea,
} from '@/components/ui/form-modal';
import type { FeedbackType } from '@/features/performance';
import {
  FEEDBACK_TYPES,
  CATEGORIES,
  RATING_OPTIONS,
} from './performance-constants';

interface FeedbackFormData {
  employeeName: string;
  type: FeedbackType | '';
  category: string;
  title: string;
  content: string;
  rating: string;
}

interface FeedbackFormModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSave: () => void;
  readonly formData: FeedbackFormData;
  readonly setFormData: (data: FeedbackFormData) => void;
  readonly isEditing: boolean;
}

export type { FeedbackFormData };

export function FeedbackFormModal({
  isOpen,
  onClose,
  onSave,
  formData,
  setFormData,
  isEditing,
}: FeedbackFormModalProps) {
  return (
    <FormModal
      title={isEditing ? 'Edit Feedback' : 'Submit Feedback'}
      description={isEditing ? 'Update feedback details' : 'Provide feedback for a team member'}
      isOpen={isOpen}
      onClose={onClose}
      onSave={onSave}
      saveLabel={isEditing ? 'Update' : 'Submit'}
      size="xl"
    >
      <div className="grid gap-4 md:grid-cols-2">
        <FormField label="Employee Name" required>
          <FormInput
            value={formData.employeeName}
            onChange={(e) => setFormData({
              ...formData,
              employeeName: e.target.value 
            })}
            placeholder="e.g., Maria Garcia"
          />
        </FormField>
        <FormField label="Feedback Type" required>
          <FormSelect
            value={formData.type}
            onChange={(e) => setFormData({
              ...formData,
              type: e.target.value as FeedbackType 
            })}
            placeholder="Select type..."
            options={FEEDBACK_TYPES}
          />
        </FormField>
        <FormField label="Category" required>
          <FormSelect
            value={formData.category}
            onChange={(e) => setFormData({
              ...formData,
              category: e.target.value 
            })}
            placeholder="Select category..."
            options={CATEGORIES}
          />
        </FormField>
        <FormField label="Rating (1-5)">
          <FormSelect
            value={formData.rating}
            onChange={(e) => setFormData({
              ...formData,
              rating: e.target.value 
            })}
            placeholder="Select rating..."
            options={RATING_OPTIONS}
          />
        </FormField>
      </div>
      <FormField label="Title" required>
        <FormInput
          value={formData.title}
          onChange={(e) => setFormData({
            ...formData,
            title: e.target.value 
          })}
          placeholder="Brief summary of the feedback"
        />
      </FormField>
      <FormField label="Details" required>
        <FormTextarea
          value={formData.content}
          onChange={(e) => setFormData({
            ...formData,
            content: e.target.value 
          })}
          placeholder="Provide specific details, examples, and context..."
          rows={4}
        />
      </FormField>
    </FormModal>
  );
}
